import { useRef, useState, useCallback } from "react";
import {
  Anchor,
  Box,
  Button,
  Center,
  Container,
  Divider,
  FileButton,
  Group,
  Image,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { Link } from "react-router-dom";
import { IconUpload, IconTrash, IconEraser } from "@tabler/icons-react";
import { useAuthStore } from "../store/auth.store";
import { useUser, useUpdateUser } from "../hooks/admin/useUsers";
import { useHasPermission } from "../hooks/useHasPermission";
import UserForm, { UserFormValues } from "../components/admin/users/UserForm";
import PasskeyManager from "../components/auth/PasskeyManager";
import {
  SignaturePad,
  SignaturePadRef,
} from "../components/account/SignaturePad";
import { signatureService } from "../services/signature.service";

function ProfileField({
  label,
  value,
}: {
  label: string;
  value?: string | null;
}) {
  return (
    <Box>
      <Text size="xs" c="dimmed" fw={500} tt="uppercase" mb={2}>
        {label}
      </Text>
      <Text size="sm">{value || "—"}</Text>
    </Box>
  );
}

export default function AccountSettings() {
  const profile = useAuthStore((s) => s.profile);
  const canEdit = useHasPermission("users.manage");
  const [editOpened, { open: openEdit, close: closeEdit }] =
    useDisclosure(false);
  const formRef = useRef<HTMLFormElement>(null);

  const { data: user, isLoading, refetch: refetchUser } = useUser(profile?.id);
  const updateUser = useUpdateUser();
  const [signatureUrl, setSignatureUrl] = useState<string | null>(null);
  const [sigSaving, setSigSaving] = useState(false);
  const [sigUploading, setSigUploading] = useState(false);
  const fileResetRef = useRef<() => void>(null);
  const signaturePadRef = useRef<SignaturePadRef>(null);

  // Derive current signature preview from user data or local state
  const currentSigUrl =
    signatureUrl ?? (user as any)?.signatureImageUrl ?? null;

  const uploadBlobAndSave = useCallback(
    async (blob: Blob, fileName: string, contentType: string) => {
      const { url, key } = await signatureService.getPresignedUrl(
        fileName,
        contentType
      );
      await signatureService.uploadToS3(url, blob);
      const result = await signatureService.updateMySignature(key);
      setSignatureUrl(result.signatureImageUrl);
      notifications.show({
        title: "Signature saved",
        message: "Your signature has been updated.",
        color: "green",
      });
      refetchUser();
    },
    [refetchUser]
  );

  const handlePadSave = useCallback(async () => {
    const dataUrl = signaturePadRef.current?.toDataURL();
    if (!dataUrl) return;
    setSigSaving(true);
    try {
      const res = await fetch(dataUrl);
      const blob = await res.blob();
      await uploadBlobAndSave(
        blob,
        `signature-pad-${Date.now()}.png`,
        "image/png"
      );
    } catch (err: any) {
      notifications.show({
        title: "Save failed",
        message: err?.message || "Could not save signature. Please try again.",
        color: "red",
      });
    } finally {
      setSigSaving(false);
    }
  }, [uploadBlobAndSave]);

  const handleFileUpload = useCallback(
    async (file: File | null) => {
      if (!file) return;
      if (!["image/png", "image/jpeg"].includes(file.type)) {
        notifications.show({
          title: "Invalid file",
          message: "Only PNG and JPEG files are allowed.",
          color: "red",
        });
        return;
      }
      if (file.size > 2 * 1024 * 1024) {
        notifications.show({
          title: "File too large",
          message: "File size must be less than 2 MB.",
          color: "red",
        });
        return;
      }
      setSigUploading(true);
      try {
        await uploadBlobAndSave(file, file.name, file.type);
      } catch (err: any) {
        notifications.show({
          title: "Upload failed",
          message:
            err?.message || "Could not upload signature. Please try again.",
          color: "red",
        });
      } finally {
        setSigUploading(false);
      }
      fileResetRef.current?.();
    },
    [uploadBlobAndSave]
  );

  const handleRemoveSignature = useCallback(async () => {
    setSigSaving(true);
    try {
      await signatureService.removeMySignature();
      setSignatureUrl(null);
      notifications.show({
        title: "Signature removed",
        message: "Your signature has been removed.",
        color: "green",
      });
      refetchUser();
    } catch (err: any) {
      notifications.show({
        title: "Remove failed",
        message:
          err?.message || "Could not remove signature. Please try again.",
        color: "red",
      });
    } finally {
      setSigSaving(false);
    }
  }, [refetchUser]);

  const handleSubmit = async (values: UserFormValues) => {
    if (!profile?.id) return;
    const {
      groupIds,
      credentials,
      pathologist_maximum_daily_cases: maxCases,
      ...rest
    } = values;
    try {
      await updateUser.mutateAsync(
        {
          id: profile.id,
          ...rest,
          groupIds,
          credentials: credentials || undefined,
          pathologist_maximum_daily_cases:
            maxCases === "" ? undefined : Number(maxCases) || undefined,
        },
        { onSuccess: closeEdit }
      );
    } catch {
      // Error notification is handled by the mutation's onError callback
    }
  };

  const groups =
    user?.userGroups
      ?.map((ug) => ug.group?.name ?? "")
      .filter(Boolean)
      .join(", ") || "—";

  return (
    <Container size="lg" py="xl">
      <Stack gap="xl">
        <Group justify="space-between" align="flex-end">
          <Box>
            <Title order={2}>My Account</Title>
            <Text size="sm" c="dimmed" mt="xs">
              View your profile information and manage security settings
            </Text>
          </Box>
          {canEdit && (
            <Button variant="default" onClick={openEdit}>
              Edit Profile
            </Button>
          )}
        </Group>

        {/* Profile */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Title order={4}>Profile</Title>
            <Divider />
            {isLoading ? (
              <Center py="md">
                <Loader size="sm" />
              </Center>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <ProfileField label="Full Name" value={user?.full_name} />
                <ProfileField label="Username" value={user?.username} />
                <ProfileField label="Email" value={user?.email} />
                <ProfileField label="Credentials" value={user?.credentials} />
                <ProfileField label="Role" value={user?.role?.name} />
                <ProfileField label="Facility" value={user?.facility?.name} />
                <ProfileField label="Group" value={groups} />
                <ProfileField
                  label="Max Daily Cases"
                  value={
                    user?.pathologist_maximum_daily_cases != null
                      ? String(user.pathologist_maximum_daily_cases)
                      : null
                  }
                />
              </SimpleGrid>
            )}
          </Stack>
        </Paper>

        {/* Security — Password */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Title order={4}>Password</Title>
            <Divider />
            <Text size="sm" c="dimmed">
              Change your account password.
            </Text>
            <Anchor component={Link} to="/change-password" size="sm">
              Change password
            </Anchor>
          </Stack>
        </Paper>

        {/* Signature */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Title order={4}>Signature</Title>
            <Divider />
            <Text size="sm" c="dimmed">
              Your signature appears on signed-off case reports. Draw below or
              upload an image file.
            </Text>

            {currentSigUrl && (
              <Box
                style={{
                  border: "1px solid #dee2e6",
                  borderRadius: 4,
                  padding: 8,
                  backgroundColor: "#fff",
                  textAlign: "center",
                  maxWidth: 300,
                }}
              >
                <Text size="xs" c="dimmed" mb={4}>
                  Current signature
                </Text>
                <Image
                  src={currentSigUrl}
                  alt="Your signature"
                  fit="contain"
                  style={{ maxHeight: 80 }}
                  onError={() => setSignatureUrl(null)}
                />
              </Box>
            )}

            <SignaturePad ref={signaturePadRef} />

            <Group justify="space-between">
              <Button
                variant="default"
                size="xs"
                leftSection={<IconEraser size={16} />}
                disabled={sigSaving || sigUploading}
                onClick={() => signaturePadRef.current?.clear()}
              >
                Clear
              </Button>
              <Group gap="sm">
                <Button
                  size="xs"
                  onClick={handlePadSave}
                  loading={sigSaving}
                  disabled={sigUploading}
                >
                  Save Signature
                </Button>
                <FileButton
                  resetRef={fileResetRef}
                  onChange={handleFileUpload}
                  accept="image/png,image/jpeg"
                >
                  {(props) => (
                    <Button
                      {...props}
                      variant="light"
                      leftSection={<IconUpload size={16} />}
                      loading={sigUploading}
                      disabled={sigSaving}
                      size="xs"
                    >
                      Upload Image
                    </Button>
                  )}
                </FileButton>
                {currentSigUrl && (
                  <Button
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={handleRemoveSignature}
                    disabled={sigSaving || sigUploading}
                    size="xs"
                  >
                    Remove
                  </Button>
                )}
              </Group>
            </Group>

            <Text size="xs" c="dimmed" ta="right">
              PNG or JPEG, max 2 MB
            </Text>
          </Stack>
        </Paper>

        {/* Security — Passkeys */}
        <Paper p="lg" withBorder>
          <Stack gap="md">
            <Title order={4}>Passkeys</Title>
            <Divider />
            <PasskeyManager />
          </Stack>
        </Paper>
      </Stack>

      {/* Edit Profile Modal — only shown for users with users.manage permission */}
      {canEdit && (
        <Modal
          opened={editOpened}
          onClose={closeEdit}
          title="Edit Profile"
          closeOnClickOutside={false}
          size="xl"
          styles={{ body: { padding: 0 } }}
        >
          <ScrollArea.Autosize
            mah="calc(100vh - 300px)"
            offsetScrollbars
            px="md"
            pt="md"
          >
            <UserForm
              formRef={formRef}
              initialValues={user}
              onSubmit={handleSubmit}
              isLoading={updateUser.isPending}
            />
          </ScrollArea.Autosize>
          <Box
            px="md"
            py="sm"
            style={{
              borderTop: "1px solid var(--mantine-color-default-border)",
            }}
          >
            <Group justify="space-between">
              <Button variant="default" onClick={closeEdit}>
                Cancel
              </Button>
              <Button
                loading={updateUser.isPending}
                onClick={() => {
                  formRef.current?.requestSubmit();
                }}
              >
                Save Changes
              </Button>
            </Group>
          </Box>
        </Modal>
      )}
    </Container>
  );
}
